import { api, req, setGuestJwt } from '@/lib/api/client';
import { ApiError, isApiError } from '@/lib/api/errors';
import { queryClient } from '@/lib/api/queryClient';
import { keys } from '@/lib/api/keys';
import { patchCardEverywhere } from '@/lib/api/queries';
import type { AssetCard, UploadSession } from '@/lib/api/types';
import { MAX_CONCURRENT_FILES, MAX_FILE_BYTES, MAX_PARTS_IN_FLIGHT, PART_RETRY_MAX, UPLOAD_DONE_DISMISS_MS } from '@/config/constants';
import { partBackoffMs } from '@/lib/shared';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { getRecord, patchRecord, useUploadStore, type UploadRecord, type UploadStatus } from '@/stores/upload';
import { buildTree, dirOf, filesToDropped, walkDataTransfer, type DroppedFile } from './folderWalk';
import { hashPool } from './hashPool';
import { makePreview, yieldToMain } from './preview';
import { deleteRow, flush, getHandle, listRows, persistRow, purgeOld, putHandle, rowFromRecord, type UploadRow } from './persistence';
import { insertPlaceholder, placeholderFromRecord, removePlaceholder, replacePlaceholder, updatePlaceholder } from './reconcile';
import { mimeAllowed, sniffMime } from './sniff';

export type EnqueueOpts = { assetId?: string; useRelativePaths?: boolean; guestJwt?: string; allowedMimePrefixes?: string[]; maxBytes?: number; workspaceId?: string };

type Xhrs = Map<number, XMLHttpRequest>;

/** §3 — session-wide upload manager (singleton). All store mutations go through `patchRecord`. */
export class UploadManager {
  private xhrs = new Map<string, Xhrs>();
  private tickQueued = false;
  private pollers = new Map<string, ReturnType<typeof setInterval>>();
  private dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** overridable for tests */
  now = () => Date.now();
  sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', () => this.pauseAll('offline'));
      window.addEventListener('online', () => this.resumeAll());
      const conn = (navigator as Navigator & { connection?: { saveData?: boolean; addEventListener?: (t: string, f: () => void) => void } }).connection;
      if (conn?.saveData) this.pauseAll('saveData');
      conn?.addEventListener?.('change', () => (conn.saveData ? this.pauseAll('saveData') : this.resumeAll()));
      window.addEventListener('beforeunload', () => void flush());
    }
  }

  // ---- public API --------------------------------------------------------------------------
  async enqueueDataTransfer(dt: DataTransfer, boardId: string, opts: EnqueueOpts = {}): Promise<void> {
    const dropped = await walkDataTransfer(dt);
    await this.enqueue(dropped, boardId, opts);
  }
  async enqueueFiles(files: FileList | File[], boardId: string, opts: EnqueueOpts = {}): Promise<void> {
    await this.enqueue(filesToDropped(files, !!opts.useRelativePaths), boardId, opts);
  }

  /** §3.2 step 1: folder tree → boards/batch, then enqueue each file under its mapped board. */
  async enqueue(dropped: DroppedFile[], boardId: string, opts: EnqueueOpts = {}): Promise<string[]> {
    if (dropped.length === 0) return [];
    const wsId = opts.workspaceId ?? useSessionStore.getState().workspace?.id ?? 'guest';
    const dirs = new Set(dropped.map((d) => dirOf(d.relativePath)).filter(Boolean));
    let boardByDir = new Map<string, string>();
    if (dirs.size > 0 && !opts.assetId && !opts.guestJwt) {
      try {
        const res = await req(api.POST('/workspaces/{workspaceId}/boards/batch', { params: { path: { workspaceId: wsId } }, body: { parent_board_id: boardId, tree: buildTree(dropped.map((d) => d.relativePath)) } }));
        boardByDir = new Map(res.nodes.map((n) => [n.path, n.board_id]));
        void queryClient.invalidateQueries({ queryKey: keys.boardChildren(boardId) });
        void queryClient.invalidateQueries({ queryKey: keys.board(boardId) });
      } catch (e) {
        toast({ kind: 'error', title: 'Could not create folders', body: isApiError(e) ? e.message : 'Try again', action: { label: 'Retry', onClick: () => void this.enqueue(dropped, boardId, opts) } });
        return [];
      }
    }
    const ids: string[] = [];
    const records: Record<string, UploadRecord> = {};
    for (const d of dropped) {
      const id = crypto.randomUUID();
      const target = boardByDir.get(dirOf(d.relativePath)) ?? boardId;
      const r: UploadRecord = {
        client_upload_id: id,
        file: d.file,
        handle: d.handle,
        name: d.file.name,
        size: d.file.size,
        lastModified: d.file.lastModified,
        detected_mime: d.file.type || 'application/octet-stream',
        target_board_id: target,
        workspace_id: wsId,
        asset_id: opts.assetId,
        is_new_version: !!opts.assetId,
        parts: new Map(),
        bytesSent: 0,
        status: 'pending',
        createdAt: this.now(),
        guest_jwt: opts.guestJwt,
        prepared: false,
        preview: null,
      };
      records[id] = r;
      ids.push(id);
    }
    useUploadStore.setState((s) => ({ records: { ...s.records, ...records }, order: [...s.order, ...ids], panelCollapsed: false }));
    for (const id of ids) {
      const r = records[id]!;
      if (!r.is_new_version) insertPlaceholder(queryClient, r.target_board_id, placeholderFromRecord(r));
      if (r.handle) void putHandle(id, r.handle);
    }
    // prepare sequentially-ish: sniff+validate immediately, hash in the worker pool, preview with yields
    for (const id of ids) {
      void this.prepare(id, opts);
      await yieldToMain();
    }
    return ids;
  }

  retry(id: string): void {
    const r = getRecord(id);
    if (!r) return;
    if (!r.file) {
      useUploadStore.setState((s) => ({ pendingResume: s.pendingResume.some((p) => p.client_upload_id === id) ? s.pendingResume : [...s.pendingResume, { client_upload_id: id, name: r.name, size: r.size, lastModified: r.lastModified, target_board_id: r.target_board_id }] }));
      return;
    }
    if (r.error?.code === 'size_mismatch' || r.error?.code === 'restart') {
      // §3.3 step 6: restart from initiate with a new client_upload_id
      const prevId = id;
      this.dismiss(prevId);
      void this.enqueue([{ file: r.file, relativePath: r.name }], r.target_board_id, { assetId: r.is_new_version ? r.asset_id : undefined, guestJwt: r.guest_jwt, workspaceId: r.workspace_id });
      return;
    }
    const prepared = !!r.sha256;
    this.setStatus(id, prepared ? 'pending' : 'pending', { error: undefined, prepared, parts: r.upload_id ? r.parts : new Map(), bytesSent: this.completedBytes(r) });
    if (!prepared) void this.prepare(id, {});
    else this.tick();
  }

  cancel(id: string): void {
    const r = getRecord(id);
    if (!r) return;
    this.abortXhrs(id);
    hashPool().cancel(id);
    this.stopPoll(id);
    if (r.upload_id && (r.status === 'uploading' || r.status === 'initiating' || r.status === 'completing' || r.status === 'paused')) {
      void req(api.DELETE('/uploads/{uploadId}', { params: { path: { uploadId: r.upload_id } } })).catch(() => undefined);
    }
    if (r.preview?.blobUrl) URL.revokeObjectURL(r.preview.blobUrl); // L11: nothing shows the preview after cancel
    this.setStatus(id, 'cancelled', { preview: null });
    useUploadStore.setState((s) => {
      const active = new Set(s.active);
      active.delete(id);
      return { active };
    });
    if (!r.is_new_version) removePlaceholder(queryClient, r.target_board_id, id);
    void deleteRow(id);
    this.tick();
  }

  dismiss(id: string): void {
    const r = getRecord(id);
    if (!r) return;
    if (r.status !== 'ready' && r.status !== 'failed' && r.status !== 'cancelled') return this.cancel(id);
    const t = this.dismissTimers.get(id);
    if (t) clearTimeout(t);
    this.dismissTimers.delete(id);
    if (r.status === 'failed' && !r.is_new_version) removePlaceholder(queryClient, r.target_board_id, id);
    if (r.preview?.blobUrl) URL.revokeObjectURL(r.preview.blobUrl);
    useUploadStore.setState((s) => {
      const records = { ...s.records };
      delete records[id];
      return { records, order: s.order.filter((x) => x !== id), pendingResume: s.pendingResume.filter((p) => p.client_upload_id !== id) };
    });
    void deleteRow(id);
  }

  pauseAll(reason: 'offline' | 'saveData' | 'user' = 'user'): void {
    const s = useUploadStore.getState();
    if (s.paused && s.pausedReason === reason) return;
    useUploadStore.setState({ paused: true, pausedReason: reason });
    for (const id of s.active) {
      this.abortXhrs(id);
      const r = getRecord(id);
      if (r && r.status === 'uploading') this.setStatus(id, 'paused', { bytesSent: this.completedBytes(r), parts: this.resetInflight(r.parts) });
    }
  }
  resumeAll(): void {
    if (!useUploadStore.getState().paused) return;
    if (!navigator.onLine) return;
    useUploadStore.setState({ paused: false, pausedReason: null, active: new Set() });
    for (const id of useUploadStore.getState().order) {
      const r = getRecord(id);
      if (r?.status === 'paused') this.setStatus(id, 'pending', { prepared: true });
    }
    this.tick();
  }

  /** §3.4 — reload rows for this workspace; ask for files where needed. */
  async resumeFromStorage(workspaceId: string): Promise<{ count: number }> {
    await purgeOld(this.now());
    const rows = (await listRows(workspaceId)).filter((r) => ['initiating', 'uploading', 'completing', 'processing'].includes(r.status) && !useUploadStore.getState().records[r.client_upload_id]);
    const pendingResume: UploadRecord[] = [];
    for (const row of rows) {
      if (!row.upload_id) {
        await deleteRow(row.client_upload_id);
        continue;
      }
      let server: Awaited<ReturnType<typeof this.fetchStatus>> | null;
      try {
        server = await this.fetchStatus(row.upload_id);
      } catch (e) {
        if (isApiError(e) && (e.status === 404 || e.status === 410)) {
          await deleteRow(row.client_upload_id);
          continue;
        }
        server = null; // offline: keep row, try later
      }
      if (server && (server.state === 'completed' || row.status === 'processing')) {
        // upload already finished; just drop the row (processing status will arrive via the board refetch)
        await deleteRow(row.client_upload_id);
        continue;
      }
      if (server?.state === 'aborted') {
        await deleteRow(row.client_upload_id);
        continue;
      }
      const parts = new Map<number, { etag?: string; loaded: number; attempts: number; url?: string; expires_at?: number }>();
      if (server) for (const p of server.received_parts) parts.set(p.part_number, { etag: p.etag, loaded: p.size_bytes, attempts: 0 });
      else for (const p of row.completed_parts) parts.set(p.n, { etag: p.etag, loaded: 0, attempts: 0 });
      if (server) for (const p of server.parts) parts.set(p.part_number, { ...(parts.get(p.part_number) ?? { loaded: 0, attempts: 0 }), url: p.url, expires_at: Date.parse(p.expires_at) });
      const handle = await getHandle(row.client_upload_id);
      const rec: UploadRecord = {
        client_upload_id: row.client_upload_id,
        file: null,
        handle,
        name: row.name,
        size: row.size,
        lastModified: row.lastModified,
        detected_mime: row.detected_mime,
        target_board_id: row.target_board_id,
        workspace_id: row.workspace_id,
        asset_id: row.asset_id,
        version_id: row.version_id,
        upload_id: row.upload_id,
        part_size: server?.part_size ?? row.part_size,
        part_count: server?.part_count ?? row.part_count,
        sha256: row.sha256,
        hash_kind: row.hash_kind,
        parts,
        bytesSent: 0,
        status: 'paused',
        createdAt: row.createdAt,
        is_new_version: !!row.is_new_version,
        prepared: true,
        needsFile: true,
        preview: null,
      };
      rec.bytesSent = this.completedBytes(rec);
      pendingResume.push(rec);
    }
    if (pendingResume.length === 0) return { count: 0 };
    useUploadStore.setState((s) => ({
      records: { ...s.records, ...Object.fromEntries(pendingResume.map((r) => [r.client_upload_id, r])) },
      order: [...s.order, ...pendingResume.map((r) => r.client_upload_id)],
      pendingResume: [...s.pendingResume, ...pendingResume.map((r) => ({ client_upload_id: r.client_upload_id, name: r.name, size: r.size, lastModified: r.lastModified, target_board_id: r.target_board_id }))],
    }));
    for (const r of pendingResume) if (!r.is_new_version) insertPlaceholder(queryClient, r.target_board_id, placeholderFromRecord(r));
    return { count: pendingResume.length };
  }

  /** Resume with file handles (needs a user gesture). */
  async resumeWithHandles(): Promise<number> {
    let n = 0;
    for (const p of useUploadStore.getState().pendingResume) {
      const r = getRecord(p.client_upload_id);
      const h = r?.handle;
      if (!h) continue;
      try {
        const perm = await (h as FileSystemFileHandle & { requestPermission?: (o: { mode: 'read' }) => Promise<string> }).requestPermission?.({ mode: 'read' });
        if (perm && perm !== 'granted') continue;
        const f = await h.getFile();
        if (await this.attachOne(p.client_upload_id, f)) n++;
      } catch {
        /* skip */
      }
    }
    return n;
  }

  /** §3.4 — user re-selected files; match by name+size+lastModified then verify hash. */
  async attachFiles(files: File[] | FileList): Promise<number> {
    let n = 0;
    const pend = useUploadStore.getState().pendingResume;
    for (const f of Array.from(files)) {
      const m = pend.find((p) => p.name === f.name && p.size === f.size && p.lastModified === f.lastModified) ?? pend.find((p) => p.name === f.name && p.size === f.size);
      if (!m) continue;
      if (await this.attachOne(m.client_upload_id, f)) n++;
    }
    if (n === 0 && files.length) toast({ kind: 'warning', title: 'No matching files', body: 'Pick the same files you originally uploaded.' });
    return n;
  }

  // ---- WS / server driven reconciliation (called from apply.ts) ------------------------------
  onVersionState(assetId: string, versionId: string, state: string, progress: number | null | undefined, card?: AssetCard | null, error?: { message?: string; retryable?: boolean }): void {
    const id = useUploadStore.getState().clientUploadByAsset[assetId];
    if (!id) return;
    const r = getRecord(id);
    if (!r || (r.version_id && r.version_id !== versionId)) return;
    if (state === 'ready') this.markReady(id, card ?? null);
    else if (state === 'failed') this.fail(id, { code: 'processing_failed', message: error?.message ?? 'Processing failed', retryable: !!error?.retryable });
    else if (r.status === 'processing') this.setStatus(id, 'processing', { processing_progress: progress ?? r.processing_progress });
  }
  /** `version.activated` after a new-version upload (§3.5). */
  onVersionActivated(assetId: string, card?: AssetCard | null, info?: { versionId: string; previousActiveVersionId: string | null; clientUploadId: string | null }): void {
    // the relay names the upload that produced the version; fall back to the per-asset map for older servers
    const id = (info?.clientUploadId && getRecord(info.clientUploadId) ? info.clientUploadId : null) ?? useUploadStore.getState().clientUploadByAsset[assetId];
    const r = id ? getRecord(id) : undefined;
    if (!r || !id) return;
    if (info?.versionId && r.version_id && r.version_id !== info.versionId) return; // another version of the same asset went active
    if (r.is_new_version && r.status === 'processing') this.markReady(id, card ?? null);
  }
  clientUploadFor(assetId: string): string | undefined {
    return useUploadStore.getState().clientUploadByAsset[assetId];
  }

  // ---- internals -----------------------------------------------------------------------------
  private async prepare(id: string, opts: EnqueueOpts): Promise<void> {
    const r = getRecord(id);
    if (!r?.file) return;
    const file = r.file;
    const mime = await sniffMime(file);
    patchRecord(id, { detected_mime: mime });
    const settings = useSessionStore.getState().workspace?.settings as { allowed_mime_prefixes?: string[] } | undefined;
    const prefixes = opts.allowedMimePrefixes ?? settings?.allowed_mime_prefixes;
    const maxBytes = opts.maxBytes ?? MAX_FILE_BYTES;
    if (file.size === 0) return this.fail(id, { code: 'empty_file', message: 'File is empty', retryable: false });
    if (file.size > maxBytes) return this.fail(id, { code: 'too_large', message: `Larger than the ${Math.round(maxBytes / 1024 ** 3)} GiB limit`, retryable: false });
    if (!mimeAllowed(mime, prefixes)) return this.fail(id, { code: 'mime_not_allowed', message: `${mime} is not allowed in this workspace`, retryable: false });
    this.setStatus(id, 'hashing');
    const hashP = hashPool().hash(id, file, (p) => {
      const cur = getRecord(id);
      if (cur && cur.status === 'hashing' && Math.abs((cur.hash_progress ?? 0) - p) >= 0.05) this.patchAndSync(id, { hash_progress: p });
    });
    const preview = await makePreview(file, mime);
    if (preview) this.patchAndSync(id, { preview });
    try {
      const h = await hashP;
      const cur = getRecord(id);
      if (!cur || cur.status === 'cancelled') return;
      this.setStatus(id, 'pending', { sha256: h.sha256, hash_kind: h.hash_kind, hash_progress: 1, prepared: true });
      this.tick();
    } catch (e) {
      if ((e as Error).message === 'cancelled') return;
      this.fail(id, { code: 'hash_failed', message: 'Could not read the file', retryable: true });
    }
  }

  /** §3.3 — debounced to a microtask. */
  tick(): void {
    if (this.tickQueued) return;
    this.tickQueued = true;
    queueMicrotask(() => {
      this.tickQueued = false;
      this.runTick();
    });
  }
  private runTick(): void {
    const s = useUploadStore.getState();
    if (s.paused || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    const active = new Set(s.active);
    for (const id of s.order) {
      if (active.size >= MAX_CONCURRENT_FILES) break;
      if (active.has(id)) continue;
      const r = s.records[id];
      if (!r || r.status !== 'pending' || !r.prepared || !r.file) continue;
      active.add(id);
      void this.runUpload(id).finally(() => {
        useUploadStore.setState((st) => {
          const a = new Set(st.active);
          a.delete(id);
          return { active: a };
        });
        this.tick();
      });
    }
    if (active.size !== s.active.size) useUploadStore.setState({ active });
  }

  private async runUpload(id: string): Promise<void> {
    let r = getRecord(id);
    if (!r?.file || !r.sha256) return;
    // initiate (idempotent on client_upload_id)
    if (!r.upload_id) {
      this.setStatus(id, 'initiating');
      try {
        const session = await this.initiate(r);
        const parts = new Map(r.parts);
        for (const p of session.parts) parts.set(p.part_number, { ...(parts.get(p.part_number) ?? { loaded: 0, attempts: 0 }), url: p.url, expires_at: Date.parse(p.expires_at) });
        const status = session as UploadSession & { received_parts?: { part_number: number; etag: string; size_bytes: number }[] };
        for (const rp of status.received_parts ?? []) parts.set(rp.part_number, { ...(parts.get(rp.part_number) ?? { loaded: 0, attempts: 0 }), etag: rp.etag, loaded: rp.size_bytes });
        r = patchRecord(id, { upload_id: session.upload_id, asset_id: session.asset_id, version_id: session.version_id, part_size: session.part_size, part_count: session.part_count, parts, duplicateOf: session.existing_duplicate?.asset_id ?? null, status: 'uploading' })!;
        useUploadStore.setState((s) => ({ clientUploadByAsset: { ...s.clientUploadByAsset, [session.asset_id]: id } }));
        if (session.existing_duplicate?.asset_id) toast({ kind: 'info', title: `“${r.name}” already exists in this workspace`, body: 'Uploading anyway; the server will link the duplicate.' });
        this.sync(id);
      } catch (e) {
        const err = e instanceof ApiError ? e : ApiError.network(e);
        if (err.status === 413 || err.code === 'quota_exceeded') return this.fail(id, { code: 'quota_exceeded', message: err.message || 'Storage quota exceeded', retryable: false });
        if (err.status === 403) return this.fail(id, { code: 'forbidden', message: 'You cannot upload to this board', retryable: false });
        return this.fail(id, { code: err.code ?? 'initiate_failed', message: err.message || 'Could not start upload', retryable: true });
      }
    } else {
      this.setStatus(id, 'uploading', { bytesSent: this.completedBytes(r) });
    }
    // parts
    const ok = await this.uploadParts(id);
    if (!ok) return;
    r = getRecord(id)!;
    if (r.status !== 'uploading') return;
    // complete
    this.setStatus(id, 'completing');
    const parts = [...r.parts.entries()].filter(([, p]) => p.etag).map(([n, p]) => ({ part_number: n, etag: p.etag! })).sort((a, b) => a.part_number - b.part_number);
    try {
      await req(api.POST('/uploads/{uploadId}/complete', { params: { path: { uploadId: r.upload_id! } }, body: { parts }, headers: r.guest_jwt ? { Authorization: `Bearer ${r.guest_jwt}` } : undefined }));
    } catch (e) {
      const err = e instanceof ApiError ? e : ApiError.network(e);
      if (err.status === 422 || err.code === 'size_mismatch') return this.fail(id, { code: 'size_mismatch', message: 'Upload did not match the file; retry to restart', retryable: true });
      if (err.status === 409) {
        // parts incomplete: drop etags the server did not get and go again
        this.setStatus(id, 'pending', { prepared: true });
        this.tick();
        return;
      }
      return this.fail(id, { code: err.code ?? 'complete_failed', message: err.message || 'Could not finish upload', retryable: true });
    }
    this.setStatus(id, 'processing', { processing_progress: 0, bytesSent: r.size });
    this.startPoll(id);
  }

  private initiate(r: UploadRecord): Promise<UploadSession> {
    const base = {
      filename: r.name,
      mime: r.detected_mime,
      size_bytes: r.size,
      sha256: r.sha256!,
      hash_kind: r.hash_kind ?? 'full',
      ...(r.preview?.width ? { width: r.preview.width, height: r.preview.height } : {}),
      last_modified: new Date(r.lastModified).toISOString(),
      client_upload_id: r.client_upload_id,
    };
    if (r.guest_jwt) {
      const token = sessionStorage.getItem('guest:token') ?? '';
      const extra = JSON.parse(sessionStorage.getItem('guest:fields') ?? '{}') as { guest_name?: string; guest_email?: string };
      return req(api.POST('/public/upload/{token}/uploads', { params: { path: { token } }, body: { ...base, ...extra } })).then((s) => {
        setGuestJwt(s.guest_session_token);
        sessionStorage.setItem('guest:jwt', s.guest_session_token);
        return s;
      });
    }
    return req(api.POST('/uploads', { body: { ...base, board_id: r.target_board_id, ...(r.is_new_version && r.asset_id ? { asset_id: r.asset_id } : {}), activate_on_ready: true } }));
  }

  private fetchStatus(uploadId: string) {
    return req(api.GET('/uploads/{uploadId}', { params: { path: { uploadId } } }));
  }

  /** §3.3 step 4-5: ≤4 part PUTs in flight; returns false when the record left `uploading`. */
  private uploadParts(id: string): Promise<boolean> {
    return new Promise((resolve) => {
      const xhrs: Xhrs = new Map();
      this.xhrs.set(id, xhrs);
      let next = 1;
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        this.xhrs.delete(id);
        resolve(ok);
      };
      const pump = () => {
        const r = getRecord(id);
        if (!r || r.status !== 'uploading') return finish(false);
        const count = r.part_count ?? 0;
        while (xhrs.size < MAX_PARTS_IN_FLIGHT && next <= count) {
          const n = next++;
          if (r.parts.get(n)?.etag) continue;
          void this.putPart(id, n, xhrs).then(pump);
        }
        const cur = getRecord(id);
        if (!cur) return finish(false);
        if (xhrs.size === 0 && next > count) {
          const allDone = [...Array(count).keys()].every((i) => cur.parts.get(i + 1)?.etag);
          finish(allDone && cur.status === 'uploading');
        }
      };
      pump();
    });
  }

  private async putPart(id: string, n: number, xhrs: Xhrs): Promise<void> {
    for (;;) {
      const r = getRecord(id);
      if (!r?.file || r.status !== 'uploading') return;
      let part = r.parts.get(n) ?? { loaded: 0, attempts: 0 };
      if (!part.url || (part.expires_at && this.now() > part.expires_at - 60_000)) {
        const refreshed = await this.refreshPart(id, n);
        if (!refreshed) return;
        part = refreshed;
      }
      const start = (n - 1) * r.part_size!;
      const body = r.file.slice(start, Math.min(n * r.part_size!, r.size));
      const res = await this.xhrPut(id, n, part.url!, body, xhrs);
      const cur = getRecord(id);
      if (!cur || cur.status !== 'uploading') return;
      if (res.ok) {
        patchRecord(id, (rec) => {
          const parts = new Map(rec.parts);
          parts.set(n, { ...(parts.get(n) ?? { loaded: 0, attempts: 0 }), etag: res.etag ?? `"${n}"`, loaded: body.size });
          return { parts, bytesSent: this.completedBytes({ ...rec, parts }) + this.inflightBytes(parts, xhrs) };
        });
        this.sync(id);
        return;
      }
      if (res.aborted) return;
      if (res.status === 403) {
        const refreshed = await this.refreshPart(id, n);
        if (!refreshed) return;
        continue; // does not count as an attempt
      }
      const attempts = (cur.parts.get(n)?.attempts ?? 0) + 1;
      patchRecord(id, (rec) => {
        const parts = new Map(rec.parts);
        parts.set(n, { ...(parts.get(n) ?? { loaded: 0, attempts: 0 }), loaded: 0, attempts });
        return { parts };
      });
      if (attempts >= PART_RETRY_MAX) {
        this.abortXhrs(id);
        this.fail(id, { code: 'part_failed', message: `Part ${n} failed after ${attempts} attempts`, retryable: true });
        return;
      }
      await this.sleep(partBackoffMs(attempts));
    }
  }

  private async refreshPart(id: string, n: number) {
    const r = getRecord(id);
    if (!r?.upload_id) return null;
    try {
      const res = await req(api.POST('/uploads/{uploadId}/parts/refresh', { params: { path: { uploadId: r.upload_id } }, body: { part_numbers: [n] } }));
      const p = res.parts.find((x) => x.part_number === n);
      if (!p) return null;
      const next = { ...(r.parts.get(n) ?? { loaded: 0, attempts: 0 }), url: p.url, expires_at: Date.parse(p.expires_at) };
      patchRecord(id, (rec) => {
        const parts = new Map(rec.parts);
        parts.set(n, next);
        return { parts };
      });
      return next;
    } catch (e) {
      const err = e instanceof ApiError ? e : ApiError.network(e);
      if (err.status === 404 || err.status === 410) this.fail(id, { code: 'restart', message: 'Upload session expired; retry to restart', retryable: true });
      else this.fail(id, { code: 'refresh_failed', message: err.message || 'Could not refresh upload URL', retryable: true });
      return null;
    }
  }

  /** Injectable transport (tests replace it). */
  xhrPut(id: string, n: number, url: string, body: Blob, xhrs: Xhrs): Promise<{ ok: boolean; status: number; etag?: string | null; aborted?: boolean }> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhrs.set(n, xhr);
      xhr.open('PUT', url, true);
      xhr.upload.onprogress = (ev) => {
        if (!ev.lengthComputable) return;
        patchRecord(id, (rec) => {
          const parts = new Map(rec.parts);
          parts.set(n, { ...(parts.get(n) ?? { loaded: 0, attempts: 0 }), loaded: ev.loaded });
          return { parts, bytesSent: this.completedBytes({ ...rec, parts }) + this.inflightBytes(parts, xhrs) };
        });
        this.syncProgress(id);
      };
      const done = (res: { ok: boolean; status: number; etag?: string | null; aborted?: boolean }) => {
        xhrs.delete(n);
        resolve(res);
      };
      xhr.onload = () => done({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, etag: xhr.getResponseHeader('ETag') });
      xhr.onerror = () => done({ ok: false, status: 0 });
      xhr.ontimeout = () => done({ ok: false, status: 0 });
      xhr.onabort = () => done({ ok: false, status: 0, aborted: true });
      xhr.send(body);
    });
  }

  private abortXhrs(id: string): void {
    const m = this.xhrs.get(id);
    if (!m) return;
    for (const x of m.values()) x.abort();
    m.clear();
  }
  private resetInflight(parts: UploadRecord['parts']): UploadRecord['parts'] {
    const out = new Map(parts);
    for (const [n, p] of out) if (!p.etag) out.set(n, { ...p, loaded: 0 });
    return out;
  }
  private completedBytes(r: Pick<UploadRecord, 'parts'>): number {
    let b = 0;
    for (const p of r.parts.values()) if (p.etag) b += p.loaded;
    return b;
  }
  private inflightBytes(parts: UploadRecord['parts'], xhrs: Xhrs): number {
    let b = 0;
    for (const n of xhrs.keys()) {
      const p = parts.get(n);
      if (p && !p.etag) b += p.loaded;
    }
    return b;
  }

  private setStatus(id: string, status: UploadStatus, extra: Partial<UploadRecord> = {}): void {
    patchRecord(id, { status, ...extra });
    this.sync(id);
  }
  private fail(id: string, error: NonNullable<UploadRecord['error']>): void {
    this.abortXhrs(id);
    this.stopPoll(id);
    this.setStatus(id, 'failed', { error });
  }
  /** Push record state into the placeholder card + IndexedDB row. */
  private sync(id: string): void {
    const r = getRecord(id);
    if (!r) return;
    if (!r.is_new_version) updatePlaceholder(queryClient, r.target_board_id, placeholderFromRecord(r));
    else if (r.asset_id) {
      const card = queryClient.getQueryData<{ pages: { items: AssetCard[] }[] }>(keys.boardAssetsAll(r.target_board_id));
      void card;
    }
    if (r.status !== 'pending' && r.status !== 'hashing') persistRow(rowFromRecord(r));
  }
  private lastProgressSync = new Map<string, number>();
  private syncProgress(id: string): void {
    const t = this.now();
    if (t - (this.lastProgressSync.get(id) ?? 0) < 100) return;
    this.lastProgressSync.set(id, t);
    const r = getRecord(id);
    if (r && !r.is_new_version) updatePlaceholder(queryClient, r.target_board_id, placeholderFromRecord(r));
  }
  private patchAndSync(id: string, patch: Partial<UploadRecord>): void {
    patchRecord(id, patch);
    this.sync(id);
  }

  private markReady(id: string, card: AssetCard | null): void {
    const r = getRecord(id);
    if (!r || r.status === 'ready') return;
    this.stopPoll(id);
    patchRecord(id, { status: 'ready', processing_progress: 100 });
    if (r.is_new_version) {
      if (card) patchCardEverywhere(queryClient, card);
      if (r.asset_id) void queryClient.invalidateQueries({ queryKey: keys.asset(r.asset_id) });
      if (r.asset_id) void queryClient.invalidateQueries({ queryKey: keys.versions(r.asset_id) });
    } else if (card) {
      const replaced = replacePlaceholder(queryClient, r.target_board_id, id, { ...card, added_at: card.added_at ?? new Date().toISOString() });
      if (!replaced) void queryClient.invalidateQueries({ queryKey: keys.boardAssetsAll(r.target_board_id) });
      // non-default sorts: move to sorted position via one refetch (§3.6)
      for (const q of queryClient.getQueryCache().findAll({ queryKey: keys.boardAssetsAll(r.target_board_id) })) {
        const params = q.queryKey[2] as { sort?: string } | undefined;
        if (params?.sort && params.sort !== 'added_at:desc') void queryClient.invalidateQueries({ queryKey: q.queryKey });
      }
      if (r.preview?.blobUrl) {
        const url = r.preview.blobUrl;
        const img = new Image();
        img.onload = img.onerror = () => URL.revokeObjectURL(url);
        img.src = card.thumbnails['512'] ?? card.thumbnails['256'] ?? '';
      }
    } else if (r.asset_id) {
      void req(api.GET('/assets/{assetId}', { params: { path: { assetId: r.asset_id } } })).then((a) => this.markReadyWithAsset(id, a as unknown as AssetCard));
      patchRecord(id, { status: 'processing' });
      return;
    }
    void deleteRow(id);
    const t = setTimeout(() => this.dismiss(id), UPLOAD_DONE_DISMISS_MS);
    this.dismissTimers.set(id, t);
  }
  private markReadyWithAsset(id: string, asset: AssetCard): void {
    const r = getRecord(id);
    if (!r) return;
    const card: AssetCard = { ...asset, home_board_id: asset.home_board_id ?? r.target_board_id };
    this.markReady(id, card);
  }

  /** §3.3 step 6 — poll `GET /versions/{id}` every 5 s while the socket may be down. */
  private startPoll(id: string): void {
    this.stopPoll(id);
    const r = getRecord(id);
    if (!r?.version_id) return;
    const vid = r.version_id;
    const t = setInterval(() => {
      void req(api.GET('/versions/{versionId}', { params: { path: { versionId: vid } } }))
        .then((v) => {
          const cur = getRecord(id);
          if (!cur || cur.status !== 'processing') return this.stopPoll(id);
          if (v.state === 'ready') this.onVersionState(cur.asset_id!, vid, 'ready', 100, null);
          else if (v.state === 'failed') this.onVersionState(cur.asset_id!, vid, 'failed', null, null, { message: v.error_message ?? undefined, retryable: v.retryable ?? false });
        })
        .catch(() => undefined);
    }, 5000);
    this.pollers.set(id, t);
  }
  private stopPoll(id: string): void {
    const t = this.pollers.get(id);
    if (t) clearInterval(t);
    this.pollers.delete(id);
  }

  private async attachOne(id: string, file: File): Promise<boolean> {
    const r = getRecord(id);
    if (!r) return false;
    if (file.size !== r.size) return false;
    if (r.sha256) {
      this.patchAndSync(id, { file, status: 'hashing', needsFile: false });
      try {
        const h = await hashPool().hash(id, file, (p) => this.patchAndSync(id, { hash_progress: p }));
        if (h.sha256 !== r.sha256) {
          // treat as a new file
          this.dismiss(id);
          void this.enqueue([{ file, relativePath: file.name }], r.target_board_id, { workspaceId: r.workspace_id });
          return true;
        }
      } catch {
        return false;
      }
    }
    useUploadStore.setState((s) => ({ pendingResume: s.pendingResume.filter((p) => p.client_upload_id !== id) }));
    this.setStatus(id, 'pending', { file, prepared: true, needsFile: false, hash_progress: 1 });
    this.tick();
    return true;
  }
}

export const uploadManager = new UploadManager();
export type { UploadRow };
